import {
  CustomerPortalInvoice,
  CustomerPortalSessionData,
  InvoiceStatusEnum,
} from '@monetize/types/app';
import { ENVIRONMENT } from '@monetize/ui/config';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdOutlineFileDownload } from 'react-icons/md';
import { pdfjs } from 'react-pdf';
import { useGetCustomerPortalInvoicePdf } from '../../../../api/customerPortalService';
import { PAPER_PAGE_MAX_WIDTH } from '../../../../constants/quotes';
import {
  MBox,
  MFlex,
  MPageLoader,
  MText,
} from '../../../../components/Monetize';
import MButton from '../../../../components/Monetize/MButton';
import MCenterModal from '../../../../components/Monetize/MCenterModal';
import PdfDocument from '../../../../components/Monetize/Pdf/PdfDocument';
import { logger } from '../../../../services/logger';
import Sentry from '../../../../services/sentry';
import { getSafeFilename } from '../../../../utils';
import { downloadBlobAsFile } from '../../../../utils/download';

pdfjs.GlobalWorkerOptions.workerSrc = ENVIRONMENT.PDFJS_WORKER_URL;

interface ViewInvoiceModalProps {
  invoice: CustomerPortalInvoice;
  sessionData: CustomerPortalSessionData;
  canPayInvoices: boolean;
  onClose: () => void;
  onPay: (invoice: CustomerPortalInvoice) => void;
}

export const ViewInvoiceModal = ({
  invoice,
  sessionData,
  canPayInvoices,
  onClose,
  onPay,
}: ViewInvoiceModalProps) => {
  const [numPages, setNumPages] = useState(0);
  // Width available for the PDF page render, in px. Undefined until the body
  // container has been measured — we deliberately don't render <Page> with a
  // fallback width because react-pdf would lay out at that size and then need
  // a full re-render once the real width arrives.
  const [pageWidth, setPageWidth] = useState<number>();
  // react-pdf transfers the ArrayBuffer to its worker, so we render against a
  // clone and keep the original around for download.
  const [clonedPdf, setClonedPdf] = useState<ArrayBuffer>();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const {
    data: pdfBuffer,
    isLoading,
    isError,
  } = useGetCustomerPortalInvoicePdf(sessionData, invoice.id);

  useEffect(() => {
    if (pdfBuffer) {
      setClonedPdf(pdfBuffer.slice(0));
    }
  }, [pdfBuffer]);

  useEffect(() => {
    if (isError) {
      Sentry.logger.error('CustomerPortalPage - invoice view failed', {
        invoiceId: invoice.id,
        tenantId: sessionData.tenantId,
      });
    }
  }, [isError, invoice.id, sessionData.tenantId]);

  useEffect(
    () => () => {
      resizeObserverRef.current?.disconnect();
    },
    [],
  );

  // Callback ref: measures the container the moment the DOM node attaches AND
  // sets up a ResizeObserver so the page reflows when the viewport / modal
  // resizes. Using a callback ref (instead of useRef + useLayoutEffect) means
  // we don't depend on the modal being in a stable layout at mount time —
  // we'll always get the current width straight from the node.
  const bodyRef = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;

    if (!node) {
      return;
    }

    const measure = (width: number) => {
      if (width <= 0) {
        return;
      }
      setPageWidth((prev) =>
        prev === undefined || Math.abs(prev - width) >= 10 ? width : prev,
      );
    };

    measure(node.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      if (entry) {
        measure(entry.contentRect.width);
      }
    });
    observer.observe(node);
    resizeObserverRef.current = observer;
  }, []);

  const handleDownload = () => {
    if (!pdfBuffer) {
      return;
    }
    try {
      downloadBlobAsFile(
        pdfBuffer,
        `${getSafeFilename(`invoice-${invoice.invoiceNumber || invoice.id}`)}.pdf`,
        'application/pdf',
      );
    } catch (error) {
      logger.error('Failed to download invoice PDF:', error);
    }
  };

  const canPay = canPayInvoices && invoice.status === InvoiceStatusEnum.UNPAID;

  return (
    <MCenterModal
      isOpen
      onClose={onClose}
      modalTitle={`Invoice ${invoice.invoiceNumber}`}
      size="full"
      modalBodyProps={{ p: 0, backgroundColor: 'tGray.sidebarDark' }}
      renderFooter={() => (
        <MFlex gap={2} justifyContent="flex-end" w="100%">
          <MButton variant="cancel" minW="auto" onClick={onClose}>
            Close
          </MButton>
          <MButton
            variant="secondary"
            leftIcon={<MdOutlineFileDownload />}
            onClick={handleDownload}
            isDisabled={!pdfBuffer}
          >
            Download
          </MButton>
          {canPay && (
            <MButton
              variant="primary"
              onClick={() => onPay(invoice)}
              isDisabled={isLoading || isError}
            >
              Pay
            </MButton>
          )}
        </MFlex>
      )}
    >
      <MBox ref={bodyRef} w="100%" minH="100%" p={4}>
        {isLoading && (
          <MFlex justify="center" align="center" minH="60vh">
            <MPageLoader height="auto" />
          </MFlex>
        )}
        {isError && !isLoading && (
          <MFlex justify="center" align="center" minH="60vh">
            <MText color="tPurple.dark">Unable to load invoice</MText>
          </MFlex>
        )}
        {clonedPdf && pageWidth !== undefined && (
          <PdfDocument
            currentPdfFile={clonedPdf}
            numPages={numPages}
            onDocumentLoadSuccess={(pdf) => setNumPages(pdf?.numPages || 0)}
            textRenderer={({ str }) => str}
            zoom={1}
            size={Math.min(pageWidth, PAPER_PAGE_MAX_WIDTH)}
          />
        )}
      </MBox>
    </MCenterModal>
  );
};
